import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Mail, ArrowRight } from "lucide-react";

import { getOrderForConfirmation } from "@/lib/tickets.functions";

export const Route = createFileRoute("/tickets/confirmation/$orderId")({
  head: () => ({ meta: [{ title: "tickets confirmed — what" }] }),
  component: ConfirmationPage,
});

function formatDateLong(d: string) {
  const [y, m, day] = d.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, day)).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function operatorLabel(op: string | null) {
  if (op === "royalline") return "Royalline.fi";
  if (op === "citytour") return "Citytour.fi";
  if (op === "combo") return "Royalline.fi + Citytour.fi";
  return "";
}

function ConfirmationPage() {
  const { orderId } = Route.useParams();
  const fetchOrder = useServerFn(getOrderForConfirmation);
  const { data, isLoading } = useQuery({
    queryKey: ["ticket-confirmation", orderId],
    queryFn: () => fetchOrder({ data: { orderId } }),
  });

  if (isLoading) {
    return <div className="max-w-3xl mx-auto px-4 py-16 text-sm text-muted-foreground">loading…</div>;
  }

  if (!data?.order) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-2xl sm:text-3xl font-display font-extrabold lowercase">order not found</h1>
        <Link to="/tickets" className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em]">
          back to tickets <ArrowRight className="size-4" />
        </Link>
      </div>
    );
  }

  const paid = data.order.status === "confirmed";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 md:pt-12 pb-16">
      <div className="rounded-3xl border border-border bg-card p-6 md:p-8 mb-6">
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.2em] ${paid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>
          <CheckCircle2 className="size-3.5" /> {paid ? "confirmed" : "pending payment"}
        </div>
        <h1 className="mt-4 font-display text-4xl md:text-5xl lowercase leading-tight">
          {paid ? "you're all set" : "order received"}
        </h1>
        <p className="mt-3 text-base text-muted-foreground">
          {paid
            ? `we've sent ${data.tickets.length === 1 ? "your ticket" : `your ${data.tickets.length} tickets`} to the email you provided at checkout.`
            : "your tickets will be emailed once payment is confirmed."}
        </p>
        {paid && (
          <div className="mt-4 inline-flex items-center gap-2 text-xs text-muted-foreground">
            <Mail className="size-3.5" /> check your inbox (and the spam folder, just in case).
          </div>
        )}
      </div>

      <div className="space-y-4">
        {data.tickets.map((t) => (
          paid && t.qr_token ? (
          <Link
            key={t.id}
            to="/ticket/$token"
            params={{ token: t.qr_token }}
            className="block rounded-3xl border border-border bg-card p-5 md:p-6 hover:border-foreground transition-colors group"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  {operatorLabel(t.operator)}
                </div>
                <div className="mt-1 font-display text-xl lowercase leading-tight">{t.product_name}</div>
                <div className="mt-3 text-sm">
                  <div className="text-muted-foreground text-xs uppercase tracking-[0.18em] font-mono">valid</div>
                  <div className="font-bold mt-0.5">{formatDateLong(t.valid_date)}</div>
                </div>
                <div className="mt-3 text-sm">
                  <div className="text-muted-foreground text-xs uppercase tracking-[0.18em] font-mono">holder</div>
                  <div className="font-bold mt-0.5">{t.holder_name}</div>
                </div>
                <div className="mt-3 font-mono text-xs text-muted-foreground">
                  id · {t.shortId}
                </div>
              </div>
              <ArrowRight className="size-5 text-muted-foreground group-hover:text-foreground transition-colors mt-1 shrink-0" />
            </div>
          </Link>
          ) : (
            <div key={t.id} className="block rounded-3xl border border-border bg-card p-5 md:p-6">
              <div className="font-display text-xl lowercase leading-tight">{t.product_name}</div>
              <div className="mt-2 text-sm text-muted-foreground">{t.holder_name} · {formatDateLong(t.valid_date)}</div>
            </div>
          )
        ))}
      </div>

      <div className="mt-8 text-center">
        <Link
          to="/tickets"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
        >
          book another ticket
        </Link>
      </div>
    </div>
  );
}
